import { UserPermissions, ZonalAccess } from '../types/permissions';
import { Usuario } from '../types/auth';
import type { CampanaMaster, Campaña } from '../lib/supabase';

export function puedeAdministrarConvocatoriaLocal(usuario: Usuario | null,
  campana: Pick<Campaña, 'estado' | 'lumisial_id'> | null): boolean {
  return !!campana && ['planificacion', 'en_curso'].includes(campana.estado)
    && puedeOperarDinero(usuario, { tipo: 'lumisial', id: campana.lumisial_id,
      diocesisId: null, provinciaEclesiasticaId: null });
}

/** La convocatoria global no representa una caja; cada junta opera solo su maestra. */
export function puedeAdministrarMaestra(usuario: Usuario | null,
  maestra: Pick<CampanaMaster, 'nivel_origen' | 'diocesis_id' | 'provincia_eclesiastica_id'>): boolean {
  if (!usuario) return false;
  if (usuario.rol.nombre === 'admin_global') return true;
  if (maestra.nivel_origen === 'global') return usuario.rol.nombre === 'user_global';
  if (maestra.nivel_origen === 'diocesano') {
    return !!maestra.diocesis_id && puedeOperarDinero(usuario,
      { tipo: 'diocesis', id: maestra.diocesis_id, provinciaEclesiasticaId: maestra.provincia_eclesiastica_id ?? null });
  }
  return !!maestra.provincia_eclesiastica_id && puedeOperarDinero(usuario,
    { tipo: 'provincia', id: maestra.provincia_eclesiastica_id });
}

/** Entidad dueña de un registro de dinero. */
export type EntidadDinero =
  | { readonly tipo: 'lumisial'; readonly id: string; readonly diocesisId: string | null; readonly provinciaEclesiasticaId: string | null }
  | { readonly tipo: 'diocesis'; readonly id: string; readonly provinciaEclesiasticaId: string | null }
  | { readonly tipo: 'provincia'; readonly id: string };

/** El Lumisial que se está viendo, como dueño de dinero. */
export function entidadDeLumisial(lumisial: {
  readonly id: string;
  readonly diocesis_id: string | null;
  readonly provincia_eclesiastica_id: string | null;
}): EntidadDinero {
  return {
    tipo: 'lumisial',
    id: lumisial.id,
    diocesisId: lumisial.diocesis_id,
    provinciaEclesiasticaId: lumisial.provincia_eclesiastica_id,
  };
}

/** ¿El usuario puede registrar, editar o anular dinero del Lumisial que está viendo? */
export function puedeOperarDineroDeLumisial(
  usuario: Usuario | null,
  lumisial: Parameters<typeof entidadDeLumisial>[0] | null | undefined
): boolean {
  return lumisial != null && puedeOperarDinero(usuario, entidadDeLumisial(lumisial));
}

/**
 * Espejo de la función SQL `puede_operar_dinero`: la Junta Administrativa opera el dinero
 * de su propia entidad, igual que la Junta Sacerdotal. Los niveles inferiores son de lectura.
 */
export function puedeOperarDinero(usuario: Usuario | null, entidad: EntidadDinero): boolean {
  if (!usuario) return false;
  const rol = usuario.rol?.nombre;
  const nivel = usuario.rol?.nivel;
  if (!rol || !nivel) return false;
  if (rol === 'admin_global') return true;
  if (rol === 'user_global') return false;

  if (entidad.tipo === 'provincia') {
    return nivel === 2 && usuario.provincia_eclesiastica_id === entidad.id;
  }
  if (entidad.tipo === 'diocesis') {
    return nivel === 3 && usuario.diocesis_id === entidad.id;
  }
  return nivel === 4 && usuario.lumisial_id === entidad.id;
}

/** Nivel desde el que un usuario usa el Balance del Panel de Administración. */
export type NivelPanelBalance = 'diocesano' | 'nacional' | 'global';

/**
 * La JAD y la Junta Sacerdotal diocesana ven el balance de su diócesis; la JAN y la nacional, el
 * de su provincia; `admin_global`, todo. Null si el usuario no usa el Balance del panel.
 */
export function nivelPanelBalance(usuario: Usuario | null): NivelPanelBalance | null {
  const rol = usuario?.rol?.nombre;
  if (rol === 'admin_diocesano' || rol === 'user_diocesano') return 'diocesano';
  if (rol === 'admin_nacional' || rol === 'user_nacional') return 'nacional';
  if (rol === 'admin_global') return 'global';
  return null;
}

export function esMenuBalanceVisible(usuario: Usuario | null): boolean {
  return nivelPanelBalance(usuario) !== null;
}

/** Ámbito de las rendiciones que un usuario recibe del nivel inferior. */
export type AmbitoRecepcionBalance =
  | { readonly diocesisId: string }
  | { readonly provinciaEclesiasticaId: string };

/**
 * Espejo de `puede_recibir_balance`: la JAD y la Junta Sacerdotal diocesana reciben los balances
 * de los Lumisiales de su diócesis; la JAN y la Junta Sacerdotal nacional, los de las diócesis de
 * su provincia; `admin_global`, los de la provincia que tenga seleccionada. Null si no recibe.
 */
export function ambitoRecepcionBalance(
  usuario: Usuario | null,
  provinciaSeleccionada?: string
): AmbitoRecepcionBalance | null {
  const rol = usuario?.rol?.nombre;
  if ((rol === 'admin_diocesano' || rol === 'user_diocesano') && usuario?.diocesis_id) {
    return { diocesisId: usuario.diocesis_id };
  }
  if ((rol === 'admin_nacional' || rol === 'user_nacional') && usuario?.provincia_eclesiastica_id) {
    return { provinciaEclesiasticaId: usuario.provincia_eclesiastica_id };
  }
  if (rol === 'admin_global' && provinciaSeleccionada) {
    return { provinciaEclesiasticaId: provinciaSeleccionada };
  }
  return null;
}

export function getUserPermissions(usuario: Usuario | null): UserPermissions {
  if (!usuario) {
    return {
      canAccessAdmin: false,
      canAccessIndicators: false,
      canAccessLumisial: false,
      isReadOnly: true,
      canManageUsers: false,
      canManageGeography: false,
      canApproveSolicitudes: false,
      zoneScopeLevel: 'local',
    };
  }

  const rolNombre = usuario.rol?.nombre || '';
  const isUser = rolNombre.startsWith('user');

  const isAdminGlobal = rolNombre === 'admin_global';
  const isAdminNacional = rolNombre === 'admin_nacional';
  const isAdminDiocesano = rolNombre === 'admin_diocesano';

  const isUserGlobal = rolNombre === 'user_global';
  const isUserNacional = rolNombre === 'user_nacional';
  const isUserDiocesano = rolNombre === 'user_diocesano';

  const canAccessAdmin = isAdminGlobal || isAdminNacional || isAdminDiocesano;

  const canAccessIndicators = isAdminGlobal || isAdminNacional || isAdminDiocesano ||
                               isUserGlobal || isUserNacional || isUserDiocesano;

  let zoneScopeLevel: UserPermissions['zoneScopeLevel'] = 'local';
  if (isAdminGlobal || isUserGlobal) zoneScopeLevel = 'super';
  else if (isAdminNacional || isUserNacional) zoneScopeLevel = 'national';
  else if (isAdminDiocesano || isUserDiocesano) zoneScopeLevel = 'diocesan';

  return {
    canAccessAdmin: canAccessAdmin || canAccessIndicators,
    canAccessIndicators,
    canAccessLumisial: true,
    isReadOnly: isUser,
    canManageUsers: canAccessAdmin,
    canManageGeography: canAccessAdmin,
    canApproveSolicitudes: canAccessAdmin,
    zoneScopeLevel,
  };
}

export function getZonalAccess(usuario: Usuario | null): ZonalAccess {
  if (!usuario) {
    return {
      provinciaEclesiasticaId: null,
      diocesisId: null,
      lumisialId: null,
      canAccessMultipleLumisiales: false,
    };
  }

  const rolNombre = usuario.rol?.nombre || '';
  const isGlobal = rolNombre.includes('global');
  const isNacional = rolNombre.includes('nacional');
  const isDiocesano = rolNombre.includes('diocesano');

  // Admin Global y User Global no tienen restricciones geográficas
  if (isGlobal) {
    return {
      provinciaEclesiasticaId: null,
      diocesisId: null,
      lumisialId: null,
      canAccessMultipleLumisiales: true,
    };
  }

  // Admin Nacional y User Nacional solo restringen por provincia eclesiástica
  if (isNacional) {
    return {
      provinciaEclesiasticaId: usuario.provincia_eclesiastica_id || null,
      diocesisId: null,
      lumisialId: null,
      canAccessMultipleLumisiales: true,
    };
  }

  // Admin Diocesano y User Diocesano restringen por diócesis
  if (isDiocesano) {
    return {
      provinciaEclesiasticaId: usuario.provincia_eclesiastica_id || null,
      diocesisId: usuario.diocesis_id || null,
      lumisialId: null,
      canAccessMultipleLumisiales: true,
    };
  }

  // Admin Local y User Local solo acceden a su lumisial
  return {
    provinciaEclesiasticaId: usuario.provincia_eclesiastica_id || null,
    diocesisId: usuario.diocesis_id || null,
    lumisialId: usuario.lumisial_id || null,
    canAccessMultipleLumisiales: false,
  };
}

export function canAccessContext(usuario: Usuario | null, context: 'admin' | 'lumisial'): boolean {
  const permissions = getUserPermissions(usuario);

  switch (context) {
    case 'admin':
      return permissions.canAccessAdmin;
    case 'lumisial':
      return permissions.canAccessLumisial;
    default:
      return false;
  }
}

export function getDefaultContext(usuario: Usuario | null): 'admin' | 'lumisial' | null {
  if (!usuario) return null;

  const permissions = getUserPermissions(usuario);
  const zonalAccess = getZonalAccess(usuario);

  if (!zonalAccess.canAccessMultipleLumisiales && zonalAccess.lumisialId) {
    return 'lumisial';
  }

  if (permissions.canAccessAdmin) {
    return 'admin';
  }

  if (permissions.canAccessLumisial) {
    return 'lumisial';
  }

  return null;
}

export function shouldShowContextSelector(usuario: Usuario | null): boolean {
  const zonalAccess = getZonalAccess(usuario);
  return zonalAccess.canAccessMultipleLumisiales;
}

export function getAvailableContexts(usuario: Usuario | null): Array<'admin' | 'lumisial'> {
  const permissions = getUserPermissions(usuario);
  const contexts: Array<'admin' | 'lumisial'> = [];

  if (permissions.canAccessAdmin) {
    contexts.push('admin');
  }

  if (permissions.canAccessLumisial) {
    contexts.push('lumisial');
  }

  return contexts;
}
